import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import { Flex, Select, Skeleton, Typography } from 'antd'
import { Button, EmptyState, emptyStates, Well } from '@pushly/aqe/lib/components'
import PushlyIcon from '@pushly/aqe/lib/components/icons/pushly-icon'
import { DeliveryChannel } from '@pushly/models/lib/enums'
import { asEmptyStateProps } from './empty-state-props'
import { CuratorInfoTip } from './curator/curator-info-tip'
import { imageLoads } from './send-preview-image-probe'
import {
    NotificationPreviewSelectors,
    NotificationPreviewStage,
    useNotificationPreviewSelection,
} from '../notification-builder/elements/notification-builder-preview'
import { NotificationVariantModel } from '../../models/notification/notification-variant.model'
import { DomainDto } from '../../dtos/domain'
import type { Load } from '../../core/load'

type RunState = 'idle' | 'running' | 'ran' | 'failed'

/** What a state of the preview panel says: an icon, a title and a line under it. */
export interface ISendPreviewCopy {
    icon: ReactNode
    title: string
    description: string
}

/** A segment someone can preview for. */
export interface SendPreviewSegment {
    id: number
    name: string
    /** The domain's default segment, which the preview opens on. */
    isDefault: boolean
}

/** What an answer with nothing to draw says, in the answer's own words, such as why a page could not be read. */
export interface ISendPreviewNotice {
    title: string
    description: ReactNode
}

/** What a preview answered: the result to draw, or none, and what the feature wants said around it. */
export interface ISendPreviewOutcome<TResult> {
    /** What would be sent. None means the feature answered and had nothing to send. */
    result: TResult | null
    /** Replaces the `empty` words when there is no result, so the answer can say why in its own words. */
    notice?: ISendPreviewNotice
    /** A line under the notification that explains it, such as why the feature wrote it. */
    caption?: ReactNode
    /** A warning under the notification: the result is drawn, but it could not be fully confirmed. */
    advisory?: ReactNode
}

/**
 * What the feature's owner holds about the preview: not asked for, running, answered, or failed. Failed is the
 * service not answering; an answer with no result is the feature saying there is nothing to draw.
 */
export type SendPreviewState<TResult> = { status: 'idle' } | Load<ISendPreviewOutcome<TResult>>

export interface ISendPreviewWell<TResult> {
    domain: DomainDto
    /** The domain's segments, as the feature's owner holds them. */
    segments: Load<SendPreviewSegment[]>
    /** Asks the owner to read the segments. Called when the well opens and when someone retries. */
    onLoadSegments: () => void
    /** The preview, as the feature's owner holds it. */
    preview: SendPreviewState<TResult>
    /**
     * Asks the feature to preview for a segment. Return the run, so the Preview button holds its loading state, and
     * takes no second click, until the run settles.
     */
    onRun: (segmentId: number) => void | Promise<unknown>
    /** Asks the feature to forget the preview, because the segment or the feature's inputs changed. */
    onClear: () => void
    /** The well's title, such as "Preview a curated send". */
    title: string
    /** What the info icon beside the title says. */
    info: ReactNode
    /** The info icon's name for assistive technology: "About {infoAbout}". */
    infoAbout: string
    /** The panel before anything has run. */
    idle: ISendPreviewCopy
    /** The panel after a run that had nothing to send. */
    empty: ISendPreviewCopy
    /** The panel after a run that failed. The retry button is added. */
    failed: { title: string; description: string }
    /**
     * Everything the feature asks for besides the segment, in one slot: it sits under the segment and above the
     * Preview button. The feature owns its state and validation.
     */
    inputs?: ReactNode
    /** Whether the feature's inputs allow a run. A segment is always required as well. */
    canRun?: boolean
    /** When this changes, the result no longer describes the inputs and is cleared. */
    resetKey?: unknown
    /** The notification the result would be, as the notification builder's preview takes it. */
    toSource: (result: TResult, image: string | undefined) => NotificationVariantModel
    /** The result's image, if it has one. It is drawn once it is known to load. */
    imageOf?: (result: TResult) => string | null | undefined
}

/**
 * The image, when it is one a notification could carry: a full web address. A path with no site in it would be
 * read against this page and look for the image on the platform, so it is left out rather than drawn as a broken
 * picture.
 */
const usableImageUrl = (image: string | null | undefined): string | undefined =>
    image && /^https?:\/\//i.test(image) ? image : undefined

/**
 * The preview an AI feature offers: pick a segment, give the feature's own inputs, and see the notification it
 * would send, drawn by the notification builder's own preview on a smoked glass panel. What differs between
 * features is passed in: the words, the inputs, and the run, which each feature packages for and sends to its own
 * service. Everything else, the segment, the panel and its states, the platform and state choices, is here.
 */
export const SendPreviewWell = observer(function SendPreviewWell<TResult>({
    domain,
    segments: segmentsLoad,
    onLoadSegments,
    preview,
    onRun,
    onClear,
    title,
    info,
    infoAbout,
    idle,
    empty,
    failed,
    inputs,
    canRun = true,
    resetKey,
    toSource,
    imageOf,
}: ISendPreviewWell<TResult>) {
    const segments = segmentsLoad.status === 'ready' || segmentsLoad.status === 'refreshFailed' ? segmentsLoad.data : []
    const segmentsLoading = segmentsLoad.status === 'loading'
    const segmentsFailed = segmentsLoad.status === 'failed'
    const noSegments = segmentsLoad.status === 'ready' && segments.length === 0

    // The choice is the person's draft, so it is this component's own. The preview it asks for is the owner's.
    const [segmentId, setSegmentId] = useState<number | undefined>()

    const runState: RunState =
        preview.status === 'idle'
            ? 'idle'
            : preview.status === 'loading'
            ? 'running'
            : preview.status === 'failed'
            ? 'failed'
            : 'ran'
    const outcome = preview.status === 'ready' || preview.status === 'refreshFailed' ? preview.data : undefined
    const result = outcome?.result ?? null

    // Which platform and state to draw for. The choices sit in the well's header and are remembered per person,
    // the same as in the notification builder. The channel only decides the platform to open on.
    const openingVariant = useMemo(() => NotificationVariantModel.build({ channels: [DeliveryChannel.WEB] }), [])
    const selection = useNotificationPreviewSelection({
        level: 'domain',
        levelId: domain.id,
        domain,
        source: openingVariant,
    })

    useEffect(() => {
        onLoadSegments()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domain.id])

    // Opens on All Subscribers, the domain's default segment, unless someone has already chosen one.
    useEffect(() => {
        setSegmentId((chosen) => chosen ?? segments.find((segment) => segment.isDefault)?.id)
    }, [segments])

    // The feature's inputs changed, so what is showing no longer describes them. Not on the first render, which
    // would throw away a preview the owner already holds.
    const firstRender = useRef(true)
    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false
            return
        }
        onClear()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetKey])

    /** Run on demand rather than on selection. Each run invokes the feature's provider, so a click is the signal. */
    const start = async () => {
        if (segmentId && runState !== 'running') {
            await onRun(segmentId)
        }
    }

    // The image is shown only once it is known to load. The notification is drawn without it meanwhile, so a
    // picture that never comes costs nothing but the picture.
    const candidateImage = result && imageOf ? usableImageUrl(imageOf(result)) : undefined
    const [loadedImage, setLoadedImage] = useState<string | undefined>()

    useEffect(() => {
        setLoadedImage(undefined)

        if (!candidateImage) {
            return
        }

        let current = true
        imageLoads(candidateImage).then((loaded) => {
            if (current && loaded) {
                setLoadedImage(candidateImage)
            }
        })

        return () => {
            current = false
        }
    }, [candidateImage])

    const hasResult = runState === 'ran' && !!result
    const caption = hasResult ? outcome?.caption : undefined

    return (
        <Well
            title={
                <Flex align="center" gap={4}>
                    {title}
                    <CuratorInfoTip about={infoAbout}>{info}</CuratorInfoTip>
                </Flex>
            }
            actions={
                selection.isLoaded && (
                    <Flex gap={8}>
                        <NotificationPreviewSelectors
                            selection={selection}
                            // There is nothing to draw until a preview has been run, so the choices wait for one.
                            disabled={!hasResult}
                            platformStyle={{ width: 210 }}
                            stateStyle={{ width: 170 }}
                        />
                    </Flex>
                )
            }
            footerMode="hidden"
        >
            {/* The frame is the size container the layout answers to, so it follows the well rather than the window. */}
            <div className="ai-preview-frame">
                <div className="ai-preview-layout">
                    <Flex vertical gap={8} className="ai-preview-controls">
                        <Typography.Text id="send-preview-segment" className="ai-muted-text">
                            Segment
                        </Typography.Text>

                        <Skeleton loading={segmentsLoading} active={true} title={false} paragraph={{ rows: 1 }}>
                            {segmentsFailed && (
                                <Flex vertical gap={4} align="flex-start">
                                    <Typography.Text type="danger">The segments could not be loaded.</Typography.Text>
                                    <Button size="small" onClick={onLoadSegments}>
                                        Try again
                                    </Button>
                                </Flex>
                            )}

                            {!segmentsFailed && (
                                <Select
                                    aria-labelledby="send-preview-segment"
                                    value={segmentId}
                                    onChange={(next) => {
                                        setSegmentId(next)
                                        onClear()
                                    }}
                                    placeholder="Choose a segment"
                                    showSearch={true}
                                    optionFilterProp="label"
                                    options={segments.map((segment) => ({ value: segment.id, label: segment.name }))}
                                />
                            )}

                            {noSegments && (
                                <Typography.Text className="ai-muted-text">
                                    This domain has no segments yet, so there is no one to preview for.
                                </Typography.Text>
                            )}
                        </Skeleton>

                        {inputs}

                        <Flex>
                            <Button
                                type="primary"
                                onClick={start}
                                loading={runState === 'running'}
                                disabled={!segmentId || !canRun || runState === 'running'}
                            >
                                Preview
                            </Button>
                        </Flex>
                    </Flex>

                    {/* What the feature says about the notification sits with the controls, under Preview, and below
                        the panel once the two stack. */}
                    {caption && <div className="ai-preview-caption">{caption}</div>}

                    <Flex vertical className="ai-preview-stage">
                        {/* The glass is always there, so the well looks the same before, during and after a run. */}
                        <div className="ai-preview-render">
                            {runState === 'idle' && (
                                <EmptyState
                                    size="small"
                                    icon={idle.icon}
                                    iconVariant="chip"
                                    title={idle.title}
                                    description={idle.description}
                                />
                            )}

                            {runState === 'running' && (
                                <span className="curator-loading-mark" role="img" aria-label="Loading the preview">
                                    <PushlyIcon aria-hidden="true" />
                                </span>
                            )}

                            {runState === 'failed' && (
                                <EmptyState
                                    size="small"
                                    // No retry button: the Preview button beside it is the way to ask again.
                                    {...asEmptyStateProps(
                                        emptyStates.error({
                                            text: failed.title,
                                            description: failed.description,
                                        }),
                                    )}
                                />
                            )}

                            {runState === 'ran' && !hasResult && (
                                <EmptyState
                                    size="small"
                                    icon={empty.icon}
                                    iconVariant="chip"
                                    title={outcome?.notice?.title ?? empty.title}
                                    description={outcome?.notice?.description ?? empty.description}
                                />
                            )}

                            {hasResult && (
                                <Flex vertical align="center" gap={12}>
                                    <div className="sw-v2-notification-builder-preview">
                                        <div className="preview-wrapper-container">
                                            <NotificationPreviewStage
                                                domain={domain}
                                                source={toSource(result!, loadedImage)}
                                                platform={selection.previewPlatform}
                                                state={selection.previewState}
                                                withoutDeviceFrame={true}
                                            />
                                        </div>
                                    </div>

                                    {outcome?.advisory && (
                                        <div className="ai-preview-note ai-preview-advisory" role="note">
                                            {outcome.advisory}
                                        </div>
                                    )}
                                </Flex>
                            )}
                        </div>
                    </Flex>
                </div>
            </div>
        </Well>
    )
}) as <TResult>(props: ISendPreviewWell<TResult>) => React.ReactElement
